import { HttpHeaders } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ApiService } from '@services/api.service';
import { FormatCurrencyService } from '@services/format-currency.service';
import {
  takeUntil,
  map,
  Subject,
  catchError,
  concatMap,
  EMPTY,
  of,
  from,
} from 'rxjs';
import { Router } from '@angular/router';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateService } from '@ngx-translate/core';
import { MatDialogService } from '@services/dialogs.service';
import { ColombiaNITValidator } from '../invoice-funds/create-profile/nit-validator.helper';
import { MatStepper } from '@angular/material/stepper';
import { ChileRUTValidator } from './create-profile/rut-validator.helper';

type CurrencyAccounts = {
  balance: number;
  company: {
    companyId: string;
    country: string;
    name: string;
  };
  companyCurrencyAccountId: string;
  currencyIso: string;
  incomingBalance: number;
  lastTransaction: null;
  createdAt: Date;
  updatedAt: Date;
};

enum ProfileType {
  topUp = 'TOP_UP',
  commissionRequest = 'COMMISSION_REQUEST',
}

type CommissionProfiles = {
  id: number;
  city: string;
  companyAddress: string;
  country: string;
  phoneNumber: string;
  postalCode: string;
  profileName: string;
  province: string | undefined;
  taxId: string | undefined;
  status: 'enabled' | 'disabled';
  type: ProfileType;
};

export type CreateInvoiceProfilesDto = {
  companyAddress: string;
  country: string;
  province?: string;
  city: string;
  postalCode: string;
  phoneNumber: string;
  profileName: string;
  taxId?: string;
};

type ApiResponse<T> = {
  success: boolean;
  records: T;
  record: T;
};

interface OptionItem {
  title: string;
  subtitle: string;
  value: boolean;
  disabled?: boolean;
}

@Component({
  selector: 'ew-commissions',
  templateUrl: './commissions.component.html',
  styleUrl: './commissions.component.scss',
})
export class CommissionsComponent implements OnDestroy, OnInit {
  private destroy$ = new Subject<void>();

  loadingCurrencies: boolean = false;
  loadingCreateCommissions: boolean = false;
  lockSendSubmit: boolean = false;
  unlockSendButton: boolean = true;

  payment: FormGroup = new FormGroup({
    currencyAccount: new FormControl(null, [Validators.required]),
    amount: new FormControl(1, [
      Validators.required,
      Validators.min(0.01),
      Validators.max(5_000_000),
    ]),
    invoiceNumber: new FormControl('', [
      Validators.required,
      Validators.pattern('^[A-Za-z0-9-]{1,}$'),
    ]),
    providerName: new FormControl('', [
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(100),
    ]),
  });
  profile: FormGroup = new FormGroup({
    externalProfile: new FormControl(true, [Validators.required]),
    commissionProfileId: new FormControl(null),
  });
  commisionProfile: FormControl = new FormControl('', [Validators.required]);

  corporateProfile: FormGroup = new FormGroup({
    profileName: new FormControl('', [
      Validators.required,
      Validators.minLength(3),
    ]),
    address: new FormControl('', [Validators.required]),
    city: new FormControl('', [Validators.required]),
    country: new FormControl('', [Validators.required]),
    postalCode: new FormControl('', [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(10),
    ]),
    province: new FormControl(''),
    phoneNumber: new FormControl('', [
      Validators.required,
      Validators.minLength(10),
      Validators.maxLength(20),
    ]),
    taxId: new FormControl(''),
  });

  currencyAccounts: CurrencyAccounts[] = [];
  profiles: CommissionProfiles[] = [];
  options: OptionItem[] = [
    {
      title: 'commissions:externalProfileTitle',
      subtitle: 'commissions:externalProfileSubtitle',
      value: true,
    },
    {
      title: 'commissions:localProfileTitle',
      subtitle: 'commissions:localProfileSubtitle',
      value: false,
    },
  ];
  provinces: any[] = [];

  futureBalance!: number;
  tooltipMessage: string = 'commissions:tooltipMessage';
  errors: any;
  commission: any = null;
  commissionLinkBaseUrl: string = `${window.location.origin}/public/commission-request/`;
  publicLink: string | null = null;

  @ViewChild('stepper') stepper!: MatStepper;

  constructor(
    private API: ApiService,
    private formatCurrency: FormatCurrencyService,
    private readonly translate: TranslateService,
    private DIALOG: MatDialogRef<CommissionsComponent>,
    public matDialogService: MatDialogService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.getCurrencyAccounts();
    // this.getCommissionProfiles();

    this.payment.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        map((value) => ({
          currencyAccount: value.currencyAccount,
          amount:
            typeof value.amount === 'string'
              ? Number(value.amount)
              : value.amount,
        })),
      )
      .subscribe(({ currencyAccount, amount }) => {
        if (currencyAccount) {
          this.futureBalance = Number(currencyAccount.balance);
        }

        if (amount) {
          this.updateFutureBalance(amount);
        }
      });

    // Reset internal profile form when switching to external profile
    this.profile
      .get('externalProfile')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((isExternal: boolean) => {
        if (isExternal) {
          this.corporateProfile.reset();
        }
      });
  }

  get validations() {
    const validations = [this.payment.valid];

    if (this.showInternalProfileStep) {
      // When internal profile is selected, the second step should validate both profile and corporateProfile
      validations.push(this.profile.valid && this.corporateProfile.valid);
    } else {
      // When external profile is selected, only validate the profile form
      validations.push(this.profile.valid);
    }

    return validations;
  }

  onStepperNext(): void {
    const isExternalProfile = this.profile.get('externalProfile')?.value;

    if (isExternalProfile) {
      this.createCommission();
      return;
    }

    this.createProfile();
  }

  get isLastStep(): boolean {
    const isExternalProfile = this.profile.get('externalProfile')?.value;
    return isExternalProfile;
  }

  get showInternalProfileStep(): boolean {
    return this.profile.get('externalProfile')?.value === false;
  }

  getCurrencySymbol(currency: string): string {
    return this.formatCurrency.getCurrencySymbol(currency);
  }

  getCurrencyAccounts(): void {
    this.loadingCurrencies = true;

    this.API.get('v1/company-currency-accounts/').subscribe(
      (data: ApiResponse<CurrencyAccounts[]>) => {
        this.currencyAccounts = data.records;
        this.loadingCurrencies = false;
      },
      (error) => {
        this.loadingCurrencies = false;
      },
    );
  }

  getCommissionProfiles(): void {
    this.API.get(`v1/invoices/profile`).subscribe(
      (data: ApiResponse<CommissionProfiles[]>) => {
        this.profiles = data.records;
      },
    );
  }

  updateFutureBalance(amount: number) {
    const balance = this.payment.get('currencyAccount')?.value?.balance;
    if (balance) {
      this.futureBalance = Number(balance) + Number(amount);
      return;
    }

    this.futureBalance = Number(amount);
  }

  public copyToClipboard(value: string | null, tooltip: MatTooltip): void {
    if (value) {
      navigator.clipboard.writeText(value).then(() => {
        this.tooltipMessage = this.translate.instant(
          'bankTransfer:copyMessage',
        );

        tooltip.show();

        setTimeout(() => {
          this.tooltipMessage = 'bankTransfer:tooltipMessage';
          tooltip.hide();
        }, 2000);
      });
    }
  }

  openPaymentLink(url: string): void {
    window.open(url, '_blank');
  }

  createCommission(invoiceProfileId?: number): void {
    this.lockSendSubmit = true;
    var headers = new HttpHeaders();
    this.loadingCreateCommissions = true;
    const payload: any = {
      companyCurrencyAccountId:
        this.payment.value.currencyAccount.companyCurrencyAccountId,
      amountToSend: `${this.payment.value.amount}`,
      invoiceNumber: this.payment.value.invoiceNumber,
      providerName: this.payment.value.providerName,
      commissionLinkBaseUrl: this.commissionLinkBaseUrl,
    };

    if (invoiceProfileId !== undefined) {
      payload.invoiceProfileId = invoiceProfileId;
    }

    this.API.post(`/v1/commissions`, payload, {
      headers: headers,
    }).subscribe(
      (data: ApiResponse<any>) => {
        this.commission = data.records;
        this.generatePublicLink();
        this.loadingCreateCommissions = false;
        this.redirectToPaymentDetails();
      },
      (error) => {
        if (error.status >= 500) {
          if (error?.error?.errors && Array.isArray(error.error.errors)) {
            this.errors = error.error.errors.map((e: any) => {
              if (e.details) {
                return e.details.originalMessage;
              }

              return e.message;
            });
          }
        } else if (error.status < 500 && error.error) {
          this.errors = [error.error.message];
        }
      },
    );
  }

  createProfile(): void {
    this.loadingCreateCommissions = true;
    this.unlockSendButton = false;

    this.API.post<CreateInvoiceProfilesDto>(`/v1/invoices/profile`, {
      companyAddress: this.corporateProfile?.value.address!,
      country: this.corporateProfile?.value.country!,
      province: this.corporateProfile?.value.province!,
      city: this.corporateProfile?.value.city!,
      postalCode: this.corporateProfile?.value.postalCode!,
      phoneNumber: this.corporateProfile?.value.phoneNumber!,
      profileName: this.corporateProfile?.value.profileName!,
      taxId: this.corporateProfile?.value.taxId!,
      type: ProfileType.commissionRequest,
    }).subscribe(
      (data: any) => {
        this.loadingCreateCommissions = false;
        const profileId = data.record?.id || data.records?.id;
        this.createCommission(profileId);
      },
      (err) => {
        this.stepper.previous();
        this.API.alertService.error(
          `invoiceFunds:profileCreatedFailed - ${err.statusText}`,
        );
        this.loadingCreateCommissions = false;
        this.unlockSendButton = true;
      },
    );
  }

  /**
   * Generate public link for the created commission
   */
  generatePublicLink(): void {
    if (!this.commission?.id) {
      return;
    }

    this.API.post(`public-links`, {
      resourceType: 'commissions',
      id: this.commission.id,
    }).subscribe({
      next: (data: any) => {
        this.publicLink = data.record?.url || null;
        console.log(this.publicLink);
      },
      error: (error) => {
        console.error('Error generating public link:', error);
        // Fallback to the original commission URL if public link generation fails
        this.publicLink = this.commission.commissionRequestUrl || null;
      },
    });
  }

  redirectToPaymentDetails(): void {
    this.router.navigateByUrl(
      `/commission-request/${this.commission?.id}/details`,
    );
  }

  onCountryChange(): void {
    if (this.corporateProfile.value.country) {
      this.corporateProfile.patchValue({
        province: this.corporateProfile.value.province,
      });
    }

    this.getProvinces();
    this.updateProvinceValidators();
    this.updateTaxIdValidators();
  }

  get showInputProvince(): boolean {
    const country = this.corporateProfile.value.country;
    return country === 'ca' || country === 'us' || country === 'au';
  }

  private updateProvinceValidators(): void {
    if (this.showInputProvince) {
      this.getProvinces();
      this.corporateProfile
        .get('province')
        ?.setValidators([Validators.required]);
    } else {
      this.corporateProfile.get('province')?.setValidators([]);
    }
    this.corporateProfile.controls['province'].updateValueAndValidity();
  }

  private updateTaxIdValidators(): void {
    if (this.corporateProfile.value.country === 'br') {
      this.corporateProfile.get('taxId')?.setValidators([Validators.required]);
      this.corporateProfile.controls['taxId'].updateValueAndValidity();
    }

    if (this.corporateProfile.value.country === 'co') {
      this.corporateProfile.get('taxId')?.setValidators([Validators.required]);
      this.corporateProfile.controls['taxId'].updateValueAndValidity();

      const taxId = this.corporateProfile.get('taxId')?.value;
      const isValid = ColombiaNITValidator.validate(taxId!);

      if (!isValid) {
        this.corporateProfile.get('taxId')?.setErrors(['NIT not valid!']);
      }
    }

    if (this.corporateProfile.value.country === 'cl') {
      this.corporateProfile.get('taxId')?.setValidators([Validators.required]);
      this.corporateProfile.controls['taxId'].updateValueAndValidity();
    }
  }

  private isCustomValidatorsValid(): boolean {
    let isValid = true;

    const taxIdControl = this.corporateProfile.get('taxId');
    if (taxIdControl && this.corporateProfile.value.country === 'co') {
      if (!ColombiaNITValidator.validate(this.corporateProfile?.value.taxId)) {
        taxIdControl.setErrors({ invalidNIT: true });
        isValid = false;
      } else {
        taxIdControl.setErrors(null);
      }
      taxIdControl.markAsTouched();
    } else if (taxIdControl && this.corporateProfile.value.country === 'cl') {
      console.log('validating RUT');
      if (!ChileRUTValidator.validate(this.corporateProfile?.value.taxId)) {
        taxIdControl.setErrors({ invalidRUT: true });
        isValid = false;
      } else {
        taxIdControl.setErrors(null);
      }
      taxIdControl.markAsTouched();
    }

    return isValid;
  }

  getProvinces(): void {
    if (['us', 'ca', 'au'].includes(this.corporateProfile?.value.country!)) {
      this.API.get(
        `v1/public/payment-requests/provinces/${this.corporateProfile.value.country}`,
      ).subscribe((data: any) => {
        this.provinces = data.records;
        this.corporateProfile.controls['province'].setValidators(
          this.showInputProvince ? [Validators.required] : null,
        );
        this.corporateProfile.controls['province'].updateValueAndValidity();
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
